/**
 * Pure TEA update for the editor session (open project, active file, panels).
 *
 * Shell: `useEditorSessionStore.ts` (Pinia, writable computeds over `model`,
 * localStorage Cmds). Public store API is unchanged: each field is still
 * readable and assignable (`session.doc = x`), but every write is a Msg.
 */
import { assertNever } from "../lib/assertNever";
import { isMapFileName } from "../lib/mapDocument";
import { narrowLayoutForWidth } from "../lib/narrowLayout";
import {
  atIndexMaybe,
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
} from "../lib/maybe";
import { stripSceneFrontmatterLayers } from "../lib/platform/sceneFrontmatter";
import type { UpdateFn } from "../lib/tea";
import {
  databaseNameFromActivePath,
  type ZwChapter,
  type ZwConfig,
  type ZwFonts,
} from "../lib/zwConfig";

/** Mirrors `EditorKind` in `lib/tauri/core` (update modules may not import it). */
export type SessionEditorKind = "project";

/** Mirrors `EditorActiveTab` in `lib/tauri/core`. */
export type SessionActiveTab =
  "manuscript" | "storyboard" | "notes" | "database" | "trash";

export type TrashPreview = {
  label: string;
  path: string;
  content: string;
};

/** Resolves the active file to its chapter/scene identity (see store docs). */
export type ActiveSceneInfo = {
  folder: string;
  sceneFile: string;
  chapter: ZwChapter;
};

/** Boolean session fields that share one Msg. */
export type SessionFlagKey =
  | "ready"
  | "nativeMobileShell"
  | "outlineOpen"
  | "contextPanelOpen"
  | "outlineCollapsed"
  | "contextPanelCollapsed"
  | "storyboardView"
  | "databaseGraphView"
  | "commentsOpen"
  | "historyOpen"
  | "goalsOpen"
  | "projectSearchOpen"
  | "thesaurusDialogOpen"
  | "dirty"
  | "suppressDirty";

/** `Maybe<string>` session fields that share one Msg. */
export type SessionPathKey =
  | "rootPath"
  | "activePath"
  | "notesLastFile"
  | "databaseLastFile"
  | "manuscriptLastFile"
  | "activeCommentId"
  | "trashRestoringPath";

/** Flags mirrored to localStorage whenever they change. */
export type PersistedFlagKey =
  "outlineCollapsed" | "contextPanelCollapsed" | "databaseGraphView";

/**
 * Mobile layout: the native Android/iOS shell, or any shell narrower than the
 * breakpoint (narrow desktop window or browser).
 */
export function isMobileLayout(model: EditorSessionModel): boolean {
  return model.nativeMobileShell || model.narrowLayout;
}

export type EditorSessionModel = {
  readonly ready: boolean;
  /** Running in the single-webview Android/iOS Tauri shell. */
  readonly nativeMobileShell: boolean;
  /** Shell width is below the narrow-layout breakpoint (ZW-276). */
  readonly narrowLayout: boolean;
  readonly outlineOpen: boolean;
  readonly contextPanelOpen: boolean;
  readonly outlineCollapsed: boolean;
  readonly contextPanelCollapsed: boolean;
  readonly storyboardView: boolean;
  readonly databaseGraphView: boolean;
  readonly commentsOpen: boolean;
  readonly historyOpen: boolean;
  readonly goalsOpen: boolean;
  readonly projectSearchOpen: boolean;
  readonly thesaurusDialogOpen: boolean;
  readonly doc: string;
  readonly synopsis: string;
  readonly kind: SessionEditorKind;
  readonly rootPath: Maybe<string>;
  readonly activePath: Maybe<string>;
  readonly activeTab: Maybe<SessionActiveTab>;
  readonly collapsedChapters: string[];
  readonly collapsedActs: string[];
  readonly notesLastFile: Maybe<string>;
  readonly databaseLastFile: Maybe<string>;
  readonly manuscriptLastFile: Maybe<string>;
  readonly projectConfig: Maybe<ZwConfig>;
  readonly dirty: boolean;
  readonly imagePreviewRevision: number;
  readonly suppressDirty: boolean;
  readonly activeCommentId: Maybe<string>;
  readonly trashPreview: Maybe<TrashPreview>;
  readonly trashRestoringPath: Maybe<string>;
  readonly editorBottomInset: number;
};

/** Boot facts the shell reads from the environment (localStorage, embed flag). */
export type EditorSessionInit = {
  readonly outlineCollapsed: boolean;
  readonly contextPanelCollapsed: boolean;
  readonly databaseGraphView: boolean;
  /** Initial shell width (the window width at boot). */
  readonly initialWidth: number;
};

export function initialEditorSessionModel(
  init: EditorSessionInit,
): EditorSessionModel {
  return {
    ready: false,
    nativeMobileShell: false,
    narrowLayout: narrowLayoutForWidth(false, init.initialWidth),
    outlineOpen: false,
    contextPanelOpen: false,
    outlineCollapsed: init.outlineCollapsed,
    contextPanelCollapsed: init.contextPanelCollapsed,
    storyboardView: false,
    databaseGraphView: init.databaseGraphView,
    commentsOpen: false,
    historyOpen: false,
    goalsOpen: false,
    projectSearchOpen: false,
    thesaurusDialogOpen: false,
    doc: "",
    synopsis: "",
    kind: "project",
    rootPath: nothing,
    activePath: nothing,
    activeTab: nothing,
    collapsedChapters: [],
    collapsedActs: [],
    notesLastFile: nothing,
    databaseLastFile: nothing,
    manuscriptLastFile: nothing,
    projectConfig: nothing,
    dirty: false,
    imagePreviewRevision: 0,
    suppressDirty: false,
    activeCommentId: nothing,
    trashPreview: nothing,
    trashRestoringPath: nothing,
    editorBottomInset: 0,
  };
}

/**
 * Facts from `EditorPayload` needed to apply an open/reload into session.
 * Kept local so this module does not import `lib/tauri`.
 */
export type SessionApplyPayload = {
  readonly kind: SessionEditorKind;
  readonly rootPath: Maybe<string>;
  readonly activePath: Maybe<string>;
  readonly content: string;
  readonly project: Maybe<ZwConfig>;
  readonly synopsis: string;
  readonly activeTab: Maybe<SessionActiveTab>;
};

export type SessionApplyOpts = {
  readonly keepDoc: boolean;
  readonly navigationOnly: boolean;
};

export type EditorSessionMsg =
  | {
      readonly type: "SetFlag";
      readonly key: SessionFlagKey;
      readonly value: boolean;
    }
  | { readonly type: "SetDoc"; readonly value: string }
  | { readonly type: "SetSynopsis"; readonly value: string }
  | { readonly type: "SetKind"; readonly value: SessionEditorKind }
  | {
      readonly type: "SetPath";
      readonly key: SessionPathKey;
      readonly value: Maybe<string>;
    }
  | { readonly type: "SetActiveTab"; readonly value: Maybe<SessionActiveTab> }
  | { readonly type: "SetProjectConfig"; readonly value: Maybe<ZwConfig> }
  | { readonly type: "SetCollapsedChapters"; readonly value: string[] }
  | { readonly type: "SetCollapsedActs"; readonly value: string[] }
  | { readonly type: "SetImagePreviewRevision"; readonly value: number }
  | { readonly type: "SetTrashPreview"; readonly value: Maybe<TrashPreview> }
  | { readonly type: "SetEditorBottomInset"; readonly value: number }
  /** Debounced shell width from the layout observer (ZW-276). */
  | { readonly type: "ShellWidthMeasured"; readonly width: number }
  | {
      readonly type: "ApplyPayload";
      readonly payload: SessionApplyPayload;
      readonly opts: SessionApplyOpts;
      /** Shell-resolved when `rootChanged` (prefs or empty clear). */
      readonly collapsedChapters: readonly string[];
      readonly collapsedActs: readonly string[];
      readonly rootChanged: boolean;
    };

export type EditorSessionCmd =
  | {
      readonly kind: "persistBoolPref";
      readonly name: PersistedFlagKey;
      readonly value: boolean;
    }
  | { readonly kind: "applyFonts"; readonly fonts: ZwFonts }
  | { readonly kind: "restoreFonts" }
  | { readonly kind: "scheduleHarper" }
  | {
      readonly kind: "goalsNavigation";
      readonly relativePath: string;
      readonly doc: string;
    }
  | { readonly kind: "goalsEnsureIfNeeded" }
  | { readonly kind: "goalsResetWordCounts" }
  | { readonly kind: "goalsRootChanged" }
  | { readonly kind: "goalsRefresh" };

const NO_CMDS: readonly EditorSessionCmd[] = [];

function isPersistedFlag(key: SessionFlagKey): key is PersistedFlagKey {
  return (
    key === "outlineCollapsed" ||
    key === "contextPanelCollapsed" ||
    key === "databaseGraphView"
  );
}

function unchanged(
  model: EditorSessionModel,
): readonly [EditorSessionModel, readonly EditorSessionCmd[]] {
  return [model, NO_CMDS];
}

export function updateEditorSession(
  model: EditorSessionModel,
  msg: EditorSessionMsg,
): readonly [EditorSessionModel, readonly EditorSessionCmd[]] {
  switch (msg.type) {
    case "SetFlag": {
      if (model[msg.key] === msg.value) return unchanged(model);
      const next = { ...model, [msg.key]: msg.value };
      if (isPersistedFlag(msg.key)) {
        return [
          next,
          [{ kind: "persistBoolPref", name: msg.key, value: msg.value }],
        ];
      }
      return [next, NO_CMDS];
    }
    case "SetDoc":
      return model.doc === msg.value
        ? unchanged(model)
        : [{ ...model, doc: msg.value }, NO_CMDS];
    case "SetSynopsis":
      return model.synopsis === msg.value
        ? unchanged(model)
        : [{ ...model, synopsis: msg.value }, NO_CMDS];
    case "SetKind":
      // `SessionEditorKind` is currently a single literal (`"project"`), so
      // an equality check would make the update branch `never`.
      return [{ ...model, kind: msg.value }, NO_CMDS];
    case "SetPath":
      return [{ ...model, [msg.key]: msg.value }, NO_CMDS];
    case "SetActiveTab":
      return [{ ...model, activeTab: msg.value }, NO_CMDS];
    case "SetProjectConfig":
      return [{ ...model, projectConfig: msg.value }, NO_CMDS];
    case "SetCollapsedChapters":
      return [{ ...model, collapsedChapters: msg.value }, NO_CMDS];
    case "SetCollapsedActs":
      return [{ ...model, collapsedActs: msg.value }, NO_CMDS];
    case "SetImagePreviewRevision":
      return model.imagePreviewRevision === msg.value
        ? unchanged(model)
        : [{ ...model, imagePreviewRevision: msg.value }, NO_CMDS];
    case "SetTrashPreview":
      return [{ ...model, trashPreview: msg.value }, NO_CMDS];
    case "SetEditorBottomInset":
      return model.editorBottomInset === msg.value
        ? unchanged(model)
        : [{ ...model, editorBottomInset: msg.value }, NO_CMDS];
    case "ShellWidthMeasured": {
      const narrowLayout = narrowLayoutForWidth(model.narrowLayout, msg.width);
      // Drawers never reopen on their own when the layout flips back.
      return model.narrowLayout === narrowLayout
        ? unchanged(model)
        : [
            {
              ...model,
              narrowLayout,
              outlineOpen: false,
              contextPanelOpen: false,
            },
            NO_CMDS,
          ];
    }
    case "ApplyPayload": {
      const { payload, opts, rootChanged } = msg;
      let next: EditorSessionModel = {
        ...model,
        kind: payload.kind,
        rootPath: payload.rootPath,
        activePath: payload.activePath,
        activeTab: payload.activeTab,
        projectConfig: payload.project,
        activeCommentId: nothing,
        ready: true,
      };
      if (rootChanged) {
        next = {
          ...next,
          collapsedChapters: [...msg.collapsedChapters],
          collapsedActs: [...msg.collapsedActs],
        };
      }
      if (opts.keepDoc === false) {
        const { synopsis: embeddedSynopsis, body } =
          stripSceneFrontmatterLayers(payload.content);
        next = {
          ...next,
          suppressDirty: true,
          doc: body,
          synopsis:
            payload.synopsis !== "" ? payload.synopsis : embeddedSynopsis,
          dirty: false,
        };
      }

      const cmds: EditorSessionCmd[] = [];
      if (isJust(payload.project)) {
        cmds.push({ kind: "applyFonts", fonts: payload.project.value.fonts });
      } else {
        cmds.push({ kind: "restoreFonts" });
      }

      // Same-project scene switches only need the new buffer (#107).
      if (
        opts.navigationOnly &&
        isJust(payload.rootPath) &&
        rootChanged === false
      ) {
        const basename = sessionActiveBasename(next);
        const db = databaseNameFromActivePath(next.activePath);
        const activeMap = isJust(db) && isMapFileName(db.value);
        if (isJust(basename) && activeMap === false) {
          cmds.push({
            kind: "goalsNavigation",
            relativePath: basename.value,
            doc: next.doc,
          });
        }
        cmds.push({ kind: "goalsEnsureIfNeeded" });
        return [next, cmds];
      }

      cmds.push({ kind: "scheduleHarper" });
      if (isNothing(payload.rootPath)) {
        cmds.push({ kind: "goalsResetWordCounts" });
      } else if (rootChanged) {
        cmds.push({ kind: "goalsRootChanged" });
      } else {
        cmds.push({ kind: "goalsRefresh" });
      }
      return [next, cmds];
    }
    default:
      return assertNever(msg);
  }
}

export const updateEditorSessionFn: UpdateFn<
  EditorSessionModel,
  EditorSessionMsg,
  EditorSessionCmd
> = updateEditorSession;

/** Last path segment of the project root, or `"Project"` when unknown. */
export function sessionFolderName(model: EditorSessionModel): string {
  if (model.kind !== "project" || isNothing(model.rootPath)) return "Project";
  const parts = model.rootPath.value.split(/[/\\]/);
  const last = atIndexMaybe(parts, parts.length - 1);
  return isJust(last) && last.value.length > 0 ? last.value : "Project";
}

/**
 * Path of the active file relative to the project root (e.g.
 * `Chapters/Chapter 1/scene-3f9a1.md`). A renamed scene can share a basename
 * with one in another chapter, so identity is the full relative path.
 */
export function sessionActiveBasename(
  model: EditorSessionModel,
): Maybe<string> {
  if (isNothing(model.activePath) || isNothing(model.rootPath)) return nothing;
  const root = model.rootPath.value.replace(/[/\\]+$/, "");
  const active = model.activePath.value;
  if (active.startsWith(root) === false) return nothing;
  const rel = active
    .slice(root.length)
    .replace(/^[/\\]+/, "")
    .replace(/\\/g, "/");
  return rel.length > 0 ? just(rel) : nothing;
}
